import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Eye, EyeOff, User, Mail, Phone, Lock, Sparkles, ArrowLeft } from "lucide-react";
import heroImg from "@/assets/new_huge_jacuzzi.png";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const validateEmail = (val) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!val || val.trim() === "") {
      return "Email address is required";
    } else if (!emailRegex.test(val.trim())) {
      return "Please enter a valid email address (e.g. name@domain.com)";
    }
    return "";
  };

  const handleEmailChange = (val) => {
    setF((prev) => ({ ...prev, email: val }));
    if (emailTouched) {
      setEmailError(validateEmail(val));
    }
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
    setEmailError(validateEmail(f.email));
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validateEmail(f.email);
    setEmailTouched(true);
    if (err) {
      setEmailError(err);
      toast.error(err);
      return;
    }
    if (!f.name || !f.phone || !f.password) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setBusy(true);
    const r = await register(f);
    setBusy(false);
    if (r.ok) {
      toast.success("Account created successfully! Welcome to Hammam Men Spa.");
      nav("/dashboard");
    } else {
      toast.error(r.error);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-hblack text-white" data-testid="register-page">
      {/* Left Image & Value Prop Side */}
      <div className="relative hidden lg:block overflow-hidden">
        <img src={heroImg} alt="Hammam Spa Jacuzzi" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-black via-black/85 to-black/40" />

        <motion.div
          initial={{ opacity: 0, x: -80 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, ease: [0.25, 1, 0.5, 1] }}
          className="relative h-full flex flex-col justify-between p-12 lg:p-16 z-10"
        >
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-emerald-400 font-extrabold">
            <Sparkles size={16} className="text-amber-400" /> HAMMAM VIP MEMBERSHIP CONSOLE
          </div>

          <div className="space-y-6">
            <div className="font-heading text-4xl lg:text-5xl text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-emerald-400 leading-tight font-bold">
              "Every luxury ritual begins with a single choice."
            </div>
            <p className="text-white/70 text-sm leading-relaxed max-w-md font-medium">
              Create your personal Hammam account to unlock instant bonus wallet credits, priority session reservations, and exclusive male spa privileges.
            </p>

            <div className="space-y-3 pt-2 text-xs font-bold text-amber-300/90">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400" />
                <span>Instant Wallet Top-Up & Membership Perks</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-amber-400" />
                <span>100% Discreet, Private & Professional Care</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-teal-400" />
                <span>Doorstep & In-Spa Session Management</span>
              </div>
            </div>
          </div>

          <div className="text-[11px] uppercase tracking-widest text-white/40 font-semibold">
            © HAMMAM MEN SPA · EST. 2007
          </div>
        </motion.div>
      </div>

      {/* Right Registration Form Side */}
      <div className="flex items-center justify-center px-6 py-12 lg:py-16 bg-gradient-to-b from-black via-[#080808] to-black">
        <motion.form
          initial={{ opacity: 0, x: 80 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, ease: [0.25, 1, 0.5, 1] }}
          onSubmit={submit}
          className="w-full max-w-md p-8 lg:p-10 rounded-2xl bg-gradient-to-b from-hpaper via-black/90 to-hpaper border border-amber-400/70 shadow-2xl shadow-yellow-950/30 space-y-5"
          data-testid="contact-form"
        >
          <Link
            to="/"
            className="text-xs uppercase tracking-widest text-amber-400 hover:text-amber-300 font-extrabold inline-flex items-center gap-1.5 transition-colors"
          >
            <ArrowLeft size={14} /> Back to website
          </Link>

          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-400 font-extrabold mb-1">NEW VIP ACCOUNT</div>
            <h1 className="text-3xl font-heading text-transparent bg-clip-text bg-gradient-to-r from-white via-amber-200 to-emerald-400 font-bold">
              Join Hammam Spa.
            </h1>
          </div>

          {/* Full Name */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-white/90 block mb-1.5">Full Name</label>
            <div className="relative flex items-center">
              <input
                required
                placeholder="Enter your full name"
                data-testid="register-name"
                value={f.name}
                onChange={(e) => setF({ ...f, name: e.target.value })}
                className="w-full bg-black/70 border border-white/20 focus:border-amber-400 px-4 py-3 text-sm text-white outline-none rounded-xl transition-all font-medium"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-white/90 block mb-1.5">Email Address</label>
            <input
              required
              type="email"
              placeholder="name@domain.com"
              data-testid="register-email"
              value={f.email}
              onChange={(e) => handleEmailChange(e.target.value)}
              onBlur={handleEmailBlur}
              className={`w-full bg-black/70 border ${
                emailError ? "border-red-500 focus:border-red-500" : "border-white/20 focus:border-amber-400"
              } px-4 py-3 text-sm text-white outline-none rounded-xl transition-all font-medium`}
            />
            {emailError && (
              <span className="text-red-400 text-xs font-semibold mt-1.5 block animate-pulse">
                ⚠️ {emailError}
              </span>
            )}
          </div>

          {/* Phone */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-white/90 block mb-1.5">Phone Number</label>
            <input
              required
              placeholder="Mobile phone number"
              data-testid="register-phone"
              value={f.phone}
              onChange={(e) => setF({ ...f, phone: e.target.value })}
              className="w-full bg-black/70 border border-white/20 focus:border-amber-400 px-4 py-3 text-sm text-white outline-none rounded-xl transition-all font-medium"
            />
          </div>

          {/* Password */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-white/90 block mb-1.5">Password</label>
            <div className="relative flex items-center">
              <input
                required
                minLength={6}
                type={showPassword ? "text" : "password"}
                placeholder="Minimum 6 characters"
                data-testid="register-password"
                value={f.password}
                onChange={(e) => setF({ ...f, password: e.target.value })}
                className="w-full bg-black/70 border border-white/20 focus:border-amber-400 px-4 py-3 pr-12 text-sm text-white outline-none rounded-xl transition-all font-medium"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3.5 text-white/60 hover:text-amber-400 transition-colors p-1 flex items-center justify-center focus:outline-none"
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={busy}
            data-testid="register-submit"
            className="gold-btn w-full justify-center flex items-center gap-2 rounded-xl py-3.5 font-extrabold text-xs uppercase tracking-wider shadow-lg shadow-yellow-950/40"
          >
            {busy ? (
              <>
                <div className="premium-spinner animate-spin" style={{ width: "1.25rem", height: "1.25rem", borderWidth: "2px" }}></div>
                <span>Creating Account...</span>
              </>
            ) : (
              "Create Account"
            )}
          </button>

          <div className="text-sm text-white/60 text-center pt-2 font-medium">
            Already have an account?{" "}
            <Link to="/login" className="text-amber-400 hover:text-amber-300 font-bold underline">
              Sign in
            </Link>
          </div>
        </motion.form>
      </div>
    </div>
  );
}
